import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject,
  DestroyRef,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { faFileContract } from '@fortawesome/free-solid-svg-icons';

import { StudentProfile } from '../../../api/models/student-profile';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { JobCardComponent } from '../../../shared/components/network-card/job-card.component';
import { LoaderComponent } from '../../../core/loader/loader.component';
import { RoleTypes } from '../../../guards/roletypes';
import { ProfileTabCardComponent } from './profile-tab-card.component';

@Component({
  selector: 'app-student-matches-job-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NgbModule,
    JobCardComponent,
    LoaderComponent,
    ProfileTabCardComponent,
  ],
  template: `
    <app-profile-tab-card
      title="Matches jobs"
      [icon]="faFileContract"
      iconColorClass="text-success"
      [count]="viewData().totalItems"
    >
      <p class="mb-4 text-sm text-mty-body/80">
        {{
          "Below you will find the results based on the matching test you completed. Companies have indicated which competencies, personality traits and areas of expertise they find important and you have been matched on the basis of this. The matching test is new and not all companies have filled in on their side yet. So it may well be that you don't have any matches here yet. Under the jobs menu, you will find all the jobs you are matched with based on fields of expertise."
            | translate
        }}
      </p>

      @if (isLoading()) {
        <app-loader></app-loader>
      } @else if (viewData().matchJobpostings.length === 0) {
        <div
          class="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p class="mb-1">
            {{
              "We didn't find any results for you, try to find a job with different search criteria"
                | translate
            }}
          </p>
          <p class="mb-0">
            {{
              'If you update the match criteria it can take up one day to get the correct (re-)calculated match results.'
                | translate
            }}
          </p>
        </div>
      } @else {
        <div class="grid gap-4 sm:grid-cols-2">
          @for (item of viewData().matchJobpostings; track item.jobPosting?.id) {
            <app-job-card
              [data]="item.jobPosting"
              [roleType]="RoleTypes.Student"
              [showMatchPercentage]="true"
            ></app-job-card>
          }
        </div>
        <div class="mt-4 flex justify-center">
          <ngb-pagination
            (pageChange)="updatePage($event)"
            [page]="viewData().pageNumber"
            [pageSize]="viewData().pageSize"
            [collectionSize]="viewData().totalItems"
          ></ngb-pagination>
        </div>
      }
    </app-profile-tab-card>
  `,
})
export class StudentMatchesJobTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly studentProfileService = inject(StudentProfileService);

  entity = input.required<StudentProfile>();

  protected readonly faFileContract = faFileContract;

  readonly isLoading = signal(false);
  readonly viewData = signal({
    matchJobpostings: [] as any[],
    pageNumber: 1,
    pageSize: 50,
    totalItems: 0,
  });
  readonly RoleTypes = RoleTypes;

  ngOnInit() {
    this.loadMatches();
  }

  loadMatches() {
    if (!this.entity().id) return;
    this.isLoading.set(true);

    this.studentProfileService
      .getJobPostingMatches({
        profileId: this.entity().id!,
        PageSize: this.viewData().pageSize,
        PageNumber: this.viewData().pageNumber,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: any) => {
        this.isLoading.set(false);
        if (response) {
          this.viewData.update((v) => ({
            ...v,
            totalItems: response.totalItems || 0,
            matchJobpostings: response.data ? [...response.data] : [],
          }));
        }
      });
  }

  updatePage(page: number): void {
    this.viewData.update((v) => ({ ...v, pageNumber: page }));
    this.loadMatches();
  }
}
